// The palette (docs/ART-DIRECTION.md §3, docs/ROADMAP.md D34): every colour the game draws, one per colour role of the
// presentation tuning (@taxwar/tuning palette.ts), in the palette the player chose (normal or colour-blind). Its colours
// are parsed from sRGB hex into three's linear working space once per palette; consumers copy from them and never mutate
// them. A retune (the tuner) or a palette change builds a new palette, and every consumer takes it through setPalette.

import { Color } from 'three'
import { NEUTRAL_OWNER, type OwnerValue, type PlayerId } from '@taxwar/net'
import { COLOUR_ROLES, paletteColours, type ColourRole, type PaletteMode, type TuningValues } from '@taxwar/tuning'

export type Palette = Readonly<Record<ColourRole, Color>>

/** The palette `values` give in palette `mode`. */
export function paletteOf(values: TuningValues, mode: PaletteMode): Palette {
  const hexes = paletteColours(values, mode)
  const palette = {} as Record<ColourRole, Color>
  for (const role of COLOUR_ROLES) palette[role] = new Color(hexes[role])
  return palette
}

/** Owner relative to the viewing player: what the colour and rim shape encode. */
export type FactionRole = Extract<ColourRole, 'player' | 'enemy' | 'neutral'>

/**
 * Owner shape encoding (ART-DIRECTION §2.1): dash count around a city's rim ring, so owner reads without colour.
 * 0 = solid (player), few long dashes (enemy), many short dots (neutral).
 */
const RIM_DASHES: Readonly<Record<FactionRole, number>> = { player: 0, enemy: 8, neutral: 20 }

export function factionRole(owner: OwnerValue, viewer: PlayerId): FactionRole {
  if (owner === NEUTRAL_OWNER) return 'neutral'
  return owner === viewer ? 'player' : 'enemy'
}

export function factionRimDashes(role: FactionRole): number {
  return RIM_DASHES[role]
}
